import React, { useContext, useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Button,
  Avatar,
  List,
  Typography,
  Popconfirm,
  theme,
  Alert,
  Skeleton,
  message,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  NotificationOutlined,
  DeleteOutlined,
  CloseOutlined,
  CalendarOutlined,
  UserOutlined,
  EnvironmentOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  MedicineBoxOutlined,
} from '@ant-design/icons';
import { addMessagingTokenToUser } from '../login/utils';
import { StoreContext } from '../store/index';
import PlayerProfileDrawer from '../profile/playerProfileDrawer';
import { respondToInvite, addInvitedUserToShift, fetchShiftById } from '../shifts/api';
import {
  rejectRequest,
  archiveNotification,
  fetchNotificationHistory,
} from './api';

// Normalize shift URLs: backend may store /shifts/<id> (path style) but the
// app's router only has /shifts with the id as a bare query string (?<id>).
const normalizeUrl = url => {
  try {
    const { pathname, search } = new URL(url);
    const match = pathname.match(/^\/shifts\/(.+)$/);
    if (match) return `/shifts?${match[1]}`;
    return pathname + search;
  } catch {
    return '/shifts';
  }
};

const Requests = ({ visible, onClose }) => {
  const store = useContext(StoreContext);
  const {
    user: [user, , setUpdateUser],
    allRequests: [allRequests],
  } = store;
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(false);
  const [dismissingId, setDismissingId] = useState(null);
  const [history, setHistory] = useState([]);
  // Distinguish "still fetching" from "fetched and empty" so the drawer
  // shows a skeleton instead of the empty state during the initial load
  // (the history fetch pops all items at once when it resolves).
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // Tapping a notification's avatar opens the actor's profile.
  const [profilePlayerId, setProfilePlayerId] = useState(null);
  const [profileVisible, setProfileVisible] = useState(false);
  // Per-item busy flag while a Confirmar / Lesionado / No puedo is inflight.
  const [respondingId, setRespondingId] = useState(null);
  // In friendlyMode invites without a stamped side, tapping Confirmar first
  // expands into two side-choice buttons. Tracks which card is in that mode.
  const [pickingSideForId, setPickingSideForId] = useState(null);
  // The fetched shift for the card currently offering the side picker, so
  // the two buttons can show the real localTeam.name / visitTeam.name.
  const [pickingShift, setPickingShift] = useState(null);
  // Cache shifts we've already fetched to decide whether the picker is needed,
  // so tapping Confirmar on the same invite twice doesn't re-hit Firestore.
  const shiftCacheRef = useRef(new Map());

  const openProfile = playerId => {
    if (!playerId) return;
    setProfilePlayerId(playerId);
    setProfileVisible(true);
  };

  // "hace 5 min" / "hace 3 h" the same day; "ayer 20:14" or "12 ago" when older.
  const relTime = ts => {
    if (!ts?.seconds) return '';
    const then = ts.seconds * 1000;
    const now = Date.now();
    const diff = now - then;
    const MIN = 60000;
    const HOUR = 3600000;
    if (diff < MIN) return formatMessage({ id: 'notif.time.now' });
    if (diff < HOUR) return formatMessage({ id: 'notif.time.min' }, { n: Math.floor(diff / MIN) });
    const d = new Date(then);
    const nowD = new Date(now);
    if (d.toDateString() === nowD.toDateString()) {
      return formatMessage({ id: 'notif.time.hour' }, { n: Math.floor(diff / HOUR) });
    }
    const yst = new Date(now);
    yst.setDate(yst.getDate() - 1);
    if (d.toDateString() === yst.toDateString()) {
      return `${formatMessage({ id: 'notif.time.yesterday' })} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
    }
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  };

  const notifPermission = typeof Notification !== 'undefined' ? Notification.permission : 'granted';

  // For regular users allRequests is already filtered by email;
  // guard just in case admin opens this drawer
  const myRequests = (allRequests ?? []).filter(r => r.to === user?.email);

  // Load the archived notification history whenever the drawer opens.
  // On subsequent opens keep the previous list on screen and refresh in
  // background — the drawer aparecía "cargando" cada vez y el usuario
  // cerraba antes de ver los ítems.
  const loadHistory = () => {
    if (!user?.uid) return;
    const isFirstLoad = !historyLoaded;
    if (isFirstLoad) setHistoryLoading(true);
    fetchNotificationHistory(user.uid)
      .then(setHistory)
      .catch(() => {})
      .finally(() => {
        if (isFirstLoad) setHistoryLoading(false);
        setHistoryLoaded(true);
      });
  };

  useEffect(() => {
    if (visible) loadHistory();
  }, [visible, user?.uid]);

  const handleViewRequest = url => {
    navigate(normalizeUrl(url));
    onClose();
  };

  // Dismiss = archive into history, then remove from the live inbox.
  const handleDismiss = async id => {
    setDismissingId(id);
    const req = myRequests.find(r => r.id === id);
    if (req) await archiveNotification(user.uid, req).catch(() => {});
    await rejectRequest(id, setLoading);
    setDismissingId(null);
    setUpdateUser(true);
    loadHistory();
  };

  const handleClearAll = async () => {
    setLoading(true);
    await Promise.all(myRequests.map(r => archiveNotification(user.uid, r).catch(() => {})));
    await Promise.all(myRequests.map(r => rejectRequest(r.id)));
    setLoading(false);
    setUpdateUser(true);
    loadHistory();
  };

  const handleSubscribe = () => {
    addMessagingTokenToUser(user?.uid);
  };

  const isMatchInvite = req =>
    req?.pendingItem && req?.type === 'matches' && req?.shiftId;

  // Handles the 3 direct choices from the inbox card. Same pipeline as
  // ShiftResponseRow: roster mutation + response + team-side injured flag
  // when applicable. For 'confirmed' in a friendlyMode shift without a
  // pre-stamped side, the caller passes `sideHint` = 'local' | 'visit'.
  const handleRespond = async (req, choice, sideHint) => {
    if (!req?.shiftId) return;
    setRespondingId(req.id);
    try {
      const outcome = await respondToInvite(req.shiftId, user, choice, sideHint, formatMessage);
      await addInvitedUserToShift(req.shiftId, user.uid).catch(() => {});
      await archiveNotification(user.uid, req).catch(() => {});
      await rejectRequest(req.id);
      setUpdateUser(true);
      loadHistory();
      // Tell the user which bucket they landed in if it wasn't a
      // clean roster spot. Silent for injured / unavailable.
      if (choice === 'confirmed') {
        if (outcome === 'sub') message.info(formatMessage({ id: 'invited.your.joinedSub' }));
        else if (outcome === 'waitlist') message.info(formatMessage({ id: 'invited.your.waitlist' }));
      }
    } catch (err) {
      // Cuando el partido ya no existe (borrado o quedó huérfano),
      // limpiamos la notificación en vez de dejarla trabada con un
      // toast genérico de error que no permite ninguna acción.
      if (err?.message === 'Shift not found') {
        await archiveNotification(user.uid, req).catch(() => {});
        await rejectRequest(req.id).catch(() => {});
        setUpdateUser(true);
        loadHistory();
        message.info(formatMessage({ id: 'invited.shift.gone' }));
      } else {
        message.error(formatMessage({ id: 'common.save.error' }, { error: err.message || 'error' }));
      }
    } finally {
      setRespondingId(null);
      setPickingSideForId(null);
      setPickingShift(null);
    }
  };

  // Tapping Confirmar on a match invite: if the shift is friendlyMode and
  // the inviter didn't stamp a side, expand the button into two side
  // choices; otherwise fall through to the normal confirm.
  const handleConfirmClick = async req => {
    if (!req?.shiftId) return;
    setRespondingId(req.id);
    try {
      let shift = shiftCacheRef.current.get(req.shiftId);
      if (!shift) {
        const rows = await fetchShiftById(req.shiftId);
        shift = rows?.[0] ?? null;
        if (shift) shiftCacheRef.current.set(req.shiftId, shift);
      }
      // Partido borrado: no tiene sentido seguir; archivamos la notif y
      // avisamos al usuario. Mismo camino que el catch de handleRespond.
      if (!shift) {
        await archiveNotification(user.uid, req).catch(() => {});
        await rejectRequest(req.id).catch(() => {});
        setUpdateUser(true);
        loadHistory();
        setRespondingId(null);
        message.info(formatMessage({ id: 'invited.shift.gone' }));
        return;
      }
      // Show the picker on any 2-team shift — even when the inviter
      // stamped a side, that hint is only a default and the user might
      // prefer the other side or a substitute spot. Leagues (competencias
      // with real fixed rosters) and single-team shifts (one roster only)
      // skip the picker and auto-place.
      const needsPicker = !!shift
        && !shift.leagueId
        && !shift.singleTeam;
      if (needsPicker) {
        setPickingShift(shift);
        setPickingSideForId(req.id);
        setRespondingId(null);
        return;
      }
      setRespondingId(null);
      handleRespond(req, 'confirmed');
    } catch (err) {
      setRespondingId(null);
      message.error(formatMessage({ id: 'common.save.error' }, { error: err.message || 'error' }));
    }
  };

  // One unified feed: pending (actionable) + archived history, newest first.
  // Pending items are tagged so they render with their action button; those
  // without a timestamp yet sort to the top as the most recent.
  const feed = [
    ...myRequests.map(r => ({ ...r, pendingItem: true })),
    ...history.map(h => ({ ...h, pendingItem: false })),
  ].sort((a, b) => {
    const ta = a.archivedAt?.seconds ?? a.createdAt?.seconds ?? Number.MAX_SAFE_INTEGER;
    const tb = b.archivedAt?.seconds ?? b.createdAt?.seconds ?? Number.MAX_SAFE_INTEGER;
    return tb - ta;
  });

  // Renders the action column for a match-invite card. Either the 3 default
  // choices (Confirmar / Lesionado / No puedo) or, when the user tapped
  // Confirmar on a friendlyMode invite without a stamped side, the two
  // side-choice buttons using the shift's real team names.
  const renderInviteActions = item => {
    if (pickingSideForId === item.id && pickingShift) {
      // Deshabilitamos el botón cuando el equipo tiene roster + subs
      // llenos, así el jugador sabe que si acepta ese lado cae en la
      // waitlist (misma regla que PickSideModal en /players).
      const perTeam = Math.floor((pickingShift.totalPlayers ?? 0) / 2);
      const subSlots = pickingShift.substitutes ?? 0;
      const localRoster = (pickingShift.players ?? []).filter(p => p.isLocal).length;
      const visitRoster = (pickingShift.players ?? []).filter(p => !p.isLocal).length;
      const localSubs = (pickingShift.subs ?? []).filter(s => s.isLocal).length;
      const visitSubs = (pickingShift.subs ?? []).filter(s => !s.isLocal).length;
      const localFullNoSubs = localRoster >= perTeam && localSubs >= subSlots;
      const visitFullNoSubs = visitRoster >= perTeam && visitSubs >= subSlots;
      return (
        <>
          <Typography.Text
            type="secondary"
            style={{ fontSize: 11, marginBottom: 2 }}>
            {formatMessage({ id: 'invited.pickSide.title' })}
          </Typography.Text>
          <Button
            size="small"
            type="primary"
            disabled={localFullNoSubs}
            loading={respondingId === item.id}
            onClick={e => { e.stopPropagation(); handleRespond(item, 'confirmed', 'local'); }}
          >
            {formatMessage(
              { id: 'invited.pickSide.join' },
              { name: pickingShift.localTeam?.name || formatMessage({ id: 'shift.team.local' }) },
            )}
          </Button>
          <Button
            size="small"
            type="primary"
            disabled={visitFullNoSubs}
            loading={respondingId === item.id}
            onClick={e => { e.stopPropagation(); handleRespond(item, 'confirmed', 'visit'); }}
          >
            {formatMessage(
              { id: 'invited.pickSide.join' },
              { name: pickingShift.visitTeam?.name || formatMessage({ id: 'shift.team.visit' }) },
            )}
          </Button>
          <Button
            size="small"
            type="text"
            onClick={e => { e.stopPropagation(); setPickingSideForId(null); setPickingShift(null); }}
          >
            {formatMessage({ id: 'common.cancel' })}
          </Button>
        </>
      );
    }
    return (
      <>
        <Button
          size="small"
          type="primary"
          icon={<CheckOutlined />}
          loading={respondingId === item.id}
          onClick={e => { e.stopPropagation(); handleConfirmClick(item); }}
        >
          {formatMessage({ id: 'invited.opt.confirmed' })}
        </Button>
        <Button
          size="small"
          icon={<MedicineBoxOutlined />}
          loading={respondingId === item.id}
          onClick={e => { e.stopPropagation(); handleRespond(item, 'injured'); }}
        >
          {formatMessage({ id: 'invited.opt.injured' })}
        </Button>
        <Button
          size="small"
          danger
          icon={<CloseCircleOutlined />}
          loading={respondingId === item.id}
          onClick={e => { e.stopPropagation(); handleRespond(item, 'unavailable'); }}
        >
          {formatMessage({ id: 'invited.opt.unavailable' })}
        </Button>
        {/* Escape hatch: si algo falla (partido borrado, error transitorio),
            el usuario siempre puede descartar la notif con este X sin
            depender de las 3 acciones principales. */}
        <Button
          size="small"
          type="text"
          icon={<CloseOutlined />}
          loading={dismissingId === item.id}
          onClick={e => { e.stopPropagation(); handleDismiss(item.id); }}
          title={formatMessage({ id: 'notif.dismiss' })}
        />
      </>
    );
  };

  // Shared notification card: avatar → actor profile, the rest → the match.
  // `pending` items carry a dismiss button and full opacity; history is faded.
  const renderNotifCard = (item, pending) => {
    const actorId = item.actorId || null;
    const name = item.from || item.title || item.placeName || '';
    const actionLine = item.action || item.body || '';
    const when = relTime(item.archivedAt || item.createdAt);
    const dateChip = item.matchDate || item.startDate || '';
    const placeChip = item.placeName || '';

    return (
      <List.Item
        key={item.id}
        style={{
          padding: '10px 0',
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          alignItems: 'flex-start',
          opacity: pending ? 1 : 0.8,
        }}>
        <div style={{ display: 'flex', flex: 1, minWidth: 0 }}>
          {/* Avatar → opens the actor's profile (stops navigation to the match) */}
          {actorId ? (
            <div
              role="button"
              tabIndex={0}
              aria-label={name}
              onClick={e => { e.stopPropagation(); openProfile(actorId); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); openProfile(actorId); } }}
              style={{
                flexShrink: 0,
                marginRight: 10,
                marginTop: 2,
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
              }}>
              <Avatar src={item.photoURL} size={40}>
                {name ? name.charAt(0).toUpperCase() : <UserOutlined />}
              </Avatar>
            </div>
          ) : (
            <Avatar
              src={item.photoURL}
              size={40}
              style={{ flexShrink: 0, marginRight: 10, marginTop: 2 }}>
              {name ? name.charAt(0).toUpperCase() : <UserOutlined />}
            </Avatar>
          )}

          {/* Content → opens the match */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => item.url && handleViewRequest(item.url)}
            onKeyDown={e => e.key === 'Enter' && item.url && handleViewRequest(item.url)}
            style={{
              flex: 1,
              minWidth: 0,
              cursor: item.url ? 'pointer' : 'default',
              WebkitTapHighlightColor: 'transparent',
            }}>
            {/* Who + when */}
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
              <Typography.Text
                strong
                style={{ fontSize: 14, color: token.colorText, lineHeight: '1.3', minWidth: 0 }}
                ellipsis>
                {name}
              </Typography.Text>
              {when && (
                <Typography.Text style={{ fontSize: 11, color: token.colorTextTertiary, flexShrink: 0 }}>
                  {when}
                </Typography.Text>
              )}
            </div>

            {/* What happened */}
            {actionLine && (
              <Typography.Text
                type="secondary"
                style={{ fontSize: 13, display: 'block', marginTop: 1, lineHeight: '1.35' }}>
                {actionLine}
              </Typography.Text>
            )}

            {/* Match context chips: date + place */}
            {(dateChip || placeChip) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {dateChip && (
                  <span style={{
                    fontSize: 11,
                    color: token.colorTextSecondary,
                    background: token.colorFillQuaternary,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusSM,
                    padding: '1px 7px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    maxWidth: '100%',
                  }}>
                    <CalendarOutlined style={{ fontSize: 10, flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dateChip}</span>
                  </span>
                )}
                {placeChip && (
                  <span style={{
                    fontSize: 11,
                    color: token.colorTextSecondary,
                    background: token.colorFillQuaternary,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusSM,
                    padding: '1px 7px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    maxWidth: '100%',
                  }}>
                    <EnvironmentOutlined style={{ fontSize: 10, flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{placeChip}</span>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Actions (pending only). Match invites: 3 direct choices
              matching the shift's radio (Confirmado / Lesionado / No puedo);
              no reason modal, no free-text comment. Non-invite notifs
              keep the single dismiss action. */}
          {pending && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0, marginLeft: 8, marginTop: 2 }}>
              {isMatchInvite({ ...item, pendingItem: pending })
                ? renderInviteActions(item)
                : (
                  <Button
                    size="small"
                    icon={<CloseOutlined />}
                    loading={dismissingId === item.id}
                    onClick={e => { e.stopPropagation(); handleDismiss(item.id); }}
                    title={formatMessage({ id: 'notif.dismiss' })}
                  />
                )}
            </div>
          )}
        </div>
      </List.Item>
    );
  };

  return (
    <Drawer
      title={formatMessage({ id: 'requests.title' })}
      placement="right"
      onClose={onClose}
      open={visible}
      styles={{ body: { padding: '12px 16px' } }}>

      {/* Only show subscribe prompt if permission hasn't been requested yet */}
      {notifPermission === 'default' && (
        <Alert
          type="info"
          showIcon
          icon={<NotificationOutlined />}
          message={formatMessage({ id: 'requests.subscribe.prompt' })}
          action={
            <Button size="small" type="primary" onClick={handleSubscribe}>
              {formatMessage({ id: 'requests.subscribe' })}
            </Button>
          }
          style={{ marginBottom: 12, fontSize: 12 }}
        />
      )}

      {/* Clear-all: archives every pending item (only shown when some exist) */}
      {myRequests.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
          <Popconfirm
            title={formatMessage({ id: 'requests.clear.all.confirm' })}
            okText={formatMessage({ id: 'requests.clear.all' })}
            cancelText={formatMessage({ id: 'common.cancel' })}
            okButtonProps={{ danger: true }}
            onConfirm={handleClearAll}>
            <Button
              type="text"
              size="small"
              loading={loading}
              icon={<DeleteOutlined />}
              style={{ color: token.colorTextTertiary, fontSize: 12 }}>
              {formatMessage({ id: 'requests.clear.all' })}
            </Button>
          </Popconfirm>
        </div>
      )}

      {(() => {
        if (historyLoading && !historyLoaded) {
          return (
            <div style={{ padding: '4px 0' }}>
              {[0, 1, 2, 3].map(i => (
                <Skeleton
                  key={i}
                  active
                  avatar={{ size: 40 }}
                  paragraph={{ rows: 2, width: ['80%', '50%'] }}
                  title={false}
                  style={{ padding: '10px 0', borderBottom: `1px solid ${token.colorBorderSecondary}` }}
                />
              ))}
            </div>
          );
        }
        if (feed.length === 0) {
          return (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 0',
              gap: 8,
            }}>
              <NotificationOutlined style={{ fontSize: 32, color: token.colorTextQuaternary }} />
              <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                {formatMessage({ id: 'requests.empty' })}
              </Typography.Text>
            </div>
          );
        }
        return (
          <List
            dataSource={feed}
            renderItem={item => renderNotifCard(item, item.pendingItem)}
          />
        );
      })()}

      {/* Actor profile, opened by tapping a notification's avatar */}
      {profilePlayerId && (
        <PlayerProfileDrawer
          playerId={profilePlayerId}
          visible={profileVisible}
          onClose={() => setProfileVisible(false)}
        />
      )}

    </Drawer>
  );
};

export default Requests;
